import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Flex } from '@ui-library/misc';
import Button from '@ui-library/button';
import Svg from '@svg/index';
import Amplify, { Auth, Hub } from 'aws-amplify';
import { BI_EVENTS, BI_TRACKER, TRACK_EVENT_TYPES } from '@lib/utils/analytics';
// eslint-disable-next-line import/no-extraneous-dependencies
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth/lib/types';
import queryString from 'query-string';
import { WEB_APP_URL, awsConfig } from '@config';
import auth from '@services/cognito';
import { useLocation, useHistory } from 'react-router-dom';
import ErrorLabel from '@ui-library/errorLabel';
import { CustomText } from '@auth/common/styles';
import classes from '@auth/common/common.module.scss';
import { CatchErr } from '@utils/types';
import { checkIfWorkEmail, AwsUser } from '../../common/util';
import { OPENSOURCE, SIGNUP_ERROR_MESSAGES } from '../../common/constants';
import useStores from '@stores/useStores';
import LocalStorage from '@utils/localStorage';

interface Props {
  type: string | null;
}

export default ({ type }: Props) => {
  const location = useLocation();
  const history = useHistory();

  const { userStore } = useStores();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const values = queryString.parse(location.search);
    if (values.token) {
      history.push({ pathname: '/esignup', search: location.search });
      return undefined;
    }
    setErrorMessage(LocalStorage.get('authError') || '');

    // Google authentication
    Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'signIn':
          handleAuth(data);
          break;
        case 'signOut':
          break;
      }
    });

    return () => {
      LocalStorage.remove('authError');
    };
  }, []);

  useEffect(() => {
    awsConfig.Auth.oauth.redirectSignIn = WEB_APP_URL + location.pathname;
    awsConfig.Auth.oauth.redirectSignOut = WEB_APP_URL + location.pathname;
    if (type === OPENSOURCE) {
      awsConfig.Auth.oauth.redirectSignIn = `${awsConfig.Auth.oauth.redirectSignIn}?type=${OPENSOURCE}`;
      awsConfig.Auth.oauth.redirectSignOut = `${awsConfig.Auth.oauth.redirectSignOut}?type=${OPENSOURCE}`;
    }
    Amplify.configure(awsConfig);
  }, [type]);

  const validateEmailType = (email: string) => {
    if (type !== OPENSOURCE && !checkIfWorkEmail(email)) {
      throw {
        errorMessage: SIGNUP_ERROR_MESSAGES.INVALID_WORK_MAIL,
      };
    }
  };

  const handleAuth = async (user: AwsUser | null) => {
    setLoading(true);
    setErrorMessage('');
    LocalStorage.remove('authError');

    try {
      if (user) {
        const loginResp = user.signInUserSession;
        const { name, email } = loginResp.getIdToken().payload;

        validateEmailType(email);

        const provider = loginResp.getIdToken().payload.identities[0].providerName;
        const providerUserId = user.getUsername();
        const freetrial = type !== OPENSOURCE;
        const tokens = auth.getTokens(loginResp);

        const resp = await auth.authSignin(tokens.idToken, { name, provider, freetrial });
        auth.sendAnalytics(resp.firstLogin, resp.email);
        await userStore.loginUser({
          accessToken: loginResp.getAccessToken().getJwtToken(),
          idToken: loginResp.getIdToken().getJwtToken(),
          refreshToken: loginResp.getRefreshToken().getToken(),
        });
        await userStore.trackUserLogin({ firstTimeLogin: resp.firstLogin });

        await userStore.setProvider(provider, providerUserId);
        history.push('/');
      }
    } catch (error: CatchErr) {
      const message =
        error.response?.data?.message || error?.errorMessage || 'Something went wrong';
      setLoading(false);
      setErrorMessage(message);
      Auth.signOut();
      LocalStorage.set('authError', message);
    }
  };

  const onGoogleClick = () => {
    BI_TRACKER(BI_EVENTS.SIGNUP_WITH_GOOGLE, TRACK_EVENT_TYPES.ACTION, { type });
    Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Google });
  };

  const antIcon = <LoadingOutlined style={{ fontSize: 42 }} spin />;
  return (
    <Flex style={{ marginTop: '32px' }}>
      {loading ? (
        <Spin indicator={antIcon} />
      ) : (
        <div>
          <div className="text-center">
            <Flex flexDirection="column" alignItems="start">
              <Button
                {...(LocalStorage.get('authError') === null
                  ? { secondary: true }
                  : { deleteOutlined: true })}
                onClick={onGoogleClick}
                className={`${classes.inputBtn} m-b-md`}
              >
                <Flex flexDirection="row" alignItems="start">
                  <Svg name="google" />
                  <CustomText className="m-l-xl">Sign up with Google</CustomText>
                </Flex>
              </Button>
            </Flex>
          </div>
          <ErrorLabel error={errorMessage.length > 1} errorMessage={errorMessage} width="320px" />
        </div>
      )}
    </Flex>
  );
};
